import { useHasPermission } from '@app/core/auth/authorization/hooks/useHasPermission';
import { useTooltipWithHotkey } from '@app/core/hotkeys/useTooltipWithHotkey';
import { FragmentIcon } from '@app/modules/fragments/components/FragmentIcon';
import { streamHotkeys } from '@app/modules/logger/screens/stream/stream-hotkeys';
import { quickSharePermissions } from '@mi-front/api-clients';
import { Menu } from '@mi-front/ui';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Link as LinkIcon,
  PauseRounded as PauseIcon,
  PlayArrowRounded as PlayIcon,
  VideoFile as VideoFileIcon,
} from '@mui/icons-material';
import noop from 'lodash/noop';
import { useIntl } from 'react-intl';
import { isVideoStation } from '../../../../../../../../core/stations/helpers';
import { useActiveStation } from '../../../../../../../../core/stations/hooks/useActiveStation';

interface Props {
  readonly loadingAudio: boolean;
  readonly play: null | (() => void);
  readonly pause: null | (() => void);
  readonly createFragment: null | (() => void);
  readonly downloadMedia: () => void;
  readonly isPreparingDownload?: boolean;
  readonly downloadVideoTs: null | (() => void);
  readonly copyQuickShareUrl: () => void;
  readonly clear: () => void;
}

export function SelectionMenuDefaultP(p: Props) {
  const { play, createFragment, downloadMedia, downloadVideoTs, clear, copyQuickShareUrl } = p;
  const { $t } = useIntl();
  const activeStation = useActiveStation();

  return (
    <Menu
      className="el-selection-menu"
      actions={[
        {
          icon: <PlayIcon />,
          onClick: play || noop,
          tooltip: useTooltipWithHotkey(
            {
              id: 'logger.streamScreen.selectionMenu.play',
              defaultMessage: 'Reproducir selección',
            },
            streamHotkeys.get('playback', 'PLAY_PAUSE'),
          ),
          enabled: !!play,
          loading: p.loadingAudio,
        },
        {
          icon: <PauseIcon />,
          onClick: p.pause || noop,
          tooltip: useTooltipWithHotkey(
            {
              id: 'logger.streamScreen.cursorMenu.pause',
              defaultMessage: 'Pausar',
            },
            streamHotkeys.get('playback', 'PLAY_PAUSE'),
          ),
          enabled: !!p.pause,
          loading: p.loadingAudio,
        },
        {
          icon: <FragmentIcon fontSize="small" />,
          onClick: createFragment || noop,
          tooltip: useTooltipWithHotkey(
            {
              id: 'logger.streamScreen.selectionMenu.createFragment',
              defaultMessage: 'Crear fragmento',
            },
            streamHotkeys.get('selection', 'CREATE_FRAGMENT'),
          ),
          enabled: !!createFragment,
          btnProps: {
            'data-testid': 'create-fragment-btn',
          },
        },
        {
          icon: <LinkIcon fontSize="small" />,
          onClick: copyQuickShareUrl,
          tooltip: $t({
            id: 'logger.streamScreen.selectionMenu.copyQuickShare',
            defaultMessage: 'Copiar URL Quick Share',
          }),
          enabled: !!useHasPermission(quickSharePermissions.QuickShare_Share),
        },
        {
          icon: <DownloadIcon fontSize="small" />,
          onClick: downloadMedia,
          tooltip: useTooltipWithHotkey(
            {
              id: 'logger.streamScreen.selectionMenu.downloadVideo',
              defaultMessage: 'Descargar video',
            },
            streamHotkeys.get('selection', 'DOWNLOAD_SELECTION'),
          ),
          enabled: isVideoStation(activeStation),
          loading: p.isPreparingDownload ?? false,
        },
        {
          icon: <DownloadIcon fontSize="small" />,
          onClick: downloadMedia,
          tooltip: useTooltipWithHotkey(
            {
              id: 'logger.streamScreen.selectionMenu.downloadAudio',
              defaultMessage: 'Descargar audio',
            },
            streamHotkeys.get('selection', 'DOWNLOAD_SELECTION'),
          ),
          enabled: !isVideoStation(activeStation),
          loading: p.isPreparingDownload ?? false,
        },
        {
          icon: <VideoFileIcon fontSize="small" />,
          onClick: downloadVideoTs || noop,
          tooltip: 'Download TS',
          enabled: !!downloadVideoTs,
        },
        {
          icon: <CloseIcon fontSize="small" />,
          onClick: clear,
          tooltip: useTooltipWithHotkey(
            {
              id: 'logger.streamScreen.selectionMenu.unselect',
              defaultMessage: 'Deseleccionar',
            },
            streamHotkeys.get('selection', 'CLEAR_SELECTION'),
          ),
        },
      ]}
    />
  );
}
